import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ToastService } from '@shared/services/ui';
import { FileType, IPagingResponse, IResponse, ISelectItem, ListQuery } from '@shared/models';
import { DataRequest, DataRequestStatus } from '@shared/models/data-requests';
import { showBackendToast } from '@shared/operators/showBackendToast';
import { BehaviorSubject, tap } from 'rxjs';
import { DataHistories } from '@/shared/models/data-histories';
import { ContentValidationResult, DataPointValidationMessage } from '@/shared/models/content-validation-result';
import { DownloadService } from '@shared/services';
import { DataRequestContributor } from '@shared/models/data-request-contributor';

@Injectable({
  providedIn: 'root',
})
export class DataRequestService {
  private readonly http = inject(HttpClient);
  private readonly url = environment.apiBaseUrl + 'api/datarequests';
  private readonly urlSync = environment.apiBaseUrl + 'api/EnergyData';
  private readonly toastService = inject(ToastService);
  private readonly downloadService = inject(DownloadService);

  refresher$ = new BehaviorSubject<void>(void 0);
  refresherContent$ = new BehaviorSubject<void>(void 0);
  input$ = new BehaviorSubject<void>(void 0);
  upload$ = new BehaviorSubject<void>(void 0);
  validate$ = new BehaviorSubject<void>(void 0);
  failedValidationsCount$ = new BehaviorSubject<number>(0);
  private modifiedRowIds = new Set<number>();
  modifiedRowIdsSubject = new BehaviorSubject<number[]>([]);
  isDraftSaved$ = new BehaviorSubject<boolean>(false);

  addModifiedRowId(rowId: number): void {
    this.modifiedRowIds.add(rowId);
    this.modifiedRowIdsSubject.next(Array.from(this.modifiedRowIds));
  }

  getModifiedRowIds() {
    return this.modifiedRowIdsSubject.asObservable();
  }

  resetModifiedRowIds(): void {
    this.modifiedRowIds.clear();
    this.modifiedRowIdsSubject.next([]);
  }

  getByQuery(
    query: ListQuery & {
      statusIds: DataRequestStatus[];
      fundTypeIds?: number[];
      partnerInstitutionIds?: number[];
      countryIds?: number[];
      dueDate?: string;
      purposeIds?: number[];
      isOverdue?: boolean;
    },
    activeTab: number,
  ) {
    const params = { ...query } as Omit<ListQuery, 'searchTerm' | 'fundTypesIds'> &
      Partial<{
        searchTerm: string;
        fundTypeIds: number[];
        partnerInstitutionIds: number[];
        countryIds: number[];
        dueDate: string;
        purposeIds: number[];
        isOverdue?: boolean;
        statusIds: number[];
      }>;

    if (!params.searchTerm) {
      delete params.searchTerm;
    }
    if (!params.fundTypeIds || !params.fundTypeIds.length) {
      delete params.fundTypeIds;
    }
    if (!params.partnerInstitutionIds || !params.partnerInstitutionIds.length) {
      delete params.partnerInstitutionIds;
    }
    if (!params.countryIds || !params.countryIds.length) {
      delete params.countryIds;
    }
    if (!params.dueDate) {
      delete params.dueDate;
    }
    if (!params.purposeIds || !params.purposeIds.length) {
      delete params.purposeIds;
    }
    if (params.dueDate) {
      params.dueDate = this.convertDate(params.dueDate);
    }

    const allowedStatusIds = new Set(
      activeTab === 0
        ? [DataRequestStatus.NotStarted, DataRequestStatus.InProgress, DataRequestStatus.CorrectionRequired, DataRequestStatus.InReview]
        : [DataRequestStatus.Reviewed, DataRequestStatus.Completed],
    );
    const actualStatusIds = new Set(params.statusIds || []);
    // @ts-expect-error intersection is not included in TypeScript
    const intersection = allowedStatusIds.intersection(actualStatusIds);
    if (intersection.size > 0) {
      params.statusIds = Array.from(intersection);
    } else {
      params.statusIds = Array.from(allowedStatusIds);
    }
    return this.http.get<IPagingResponse<DataRequest>>(this.url, { params });
  }

  getById(id: number) {
    return this.http.get<IResponse<DataRequest>>(`${this.url}/${id}`);
  }

  getHistories(id: number, isAsc = true, orderByField = 'CreatedDate') {
    return this.http.get<IResponse<DataHistories>>(
      `${this.url}/${id}/histories?isAsc=${isAsc}&orderByField=${orderByField}&pageSize=1000&pageNumber=1`,
    );
  }

  createDataRequest(dataRequest: DataRequest) {
    return this.http.post<IResponse<unknown>>(this.url, dataRequest).pipe(showBackendToast(this.toastService));
  }

  deactivateDataRequest(id: number) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${id}/deactivate`, {})
      .pipe(showBackendToast(this.toastService));
  }

  activateDataRequest(id: number) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${id}/activate`, {})
      .pipe(showBackendToast(this.toastService));
  }

  acceptDataRequest(Id: number) {
    return this.http.put<IResponse<unknown>>(`${this.url}/${Id}/confirm`, {}).pipe(showBackendToast(this.toastService));
  }

  rejectDataRequest(Id: number, comment: string) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${Id}/reject?comment=${encodeURIComponent(comment)}`, {})
      .pipe(showBackendToast(this.toastService));
  }

  syncDataRequest(id: number, date: string) {
    return this.http
      .put<IResponse<unknown>>(`${this.urlSync}/sync/${id}?toDate=${date}`, {})
      .pipe(showBackendToast(this.toastService));
  }

  getDataRequestDataPoints(id: number) {
    return this.http.get<IResponse<ISelectItem[]>>(`${this.url}/${id}/dataPoints`);
  }

  getFailedValidationsCount(id: number) {
    return this.http.get<IResponse<number>>(`${this.url}/${id}/contents/failedValidationsCount`);
  }

  getContentsByQuery(dataRequestId: number, query: ListQuery & { statusIds: number[] }) {
    const params = { ...query } as Omit<ListQuery, 'searchTerm' | 'fundTypesIds'> &
      Partial<{
        searchTerm: string;
        fundTypesIds: number[];
        showOnlyErrors: boolean;
      }>;
    if (!params.searchTerm) {
      delete params.searchTerm;
    }
    if (!params.fundTypesIds || !params.fundTypesIds.length) {
      delete params.fundTypesIds;
    }
    if (!params.orderByField) {
      delete params.orderByField;
    }
    return this.http.get<IPagingResponse<Record<string, string | number | null | undefined>>>(
      `${this.url}/${dataRequestId}/contents`,
      {
        params,
      },
    );
  }

  uploadContent(dataRequestId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http
      .post<IResponse<unknown>>(`${this.url}/${dataRequestId}/contents/upload`, formData)
      .pipe(tap(() => this.refresher$.next()));
  }

  saveAsDraft(dataRequestId: number, dataRequestContents: Record<string, string | number | null | undefined>[]) {
    return this.http.put<IResponse<unknown>>(`${this.url}/${dataRequestId}/contents`, dataRequestContents).pipe(
      showBackendToast(this.toastService),
      tap(() => this.refresherContent$.next()),
    );
  }

  removeContent(dataRequestId: number, contentId: number) {
    return this.http.delete<IResponse<unknown>>(`${this.url}/${dataRequestId}/contents/${contentId}`).pipe(
      tap(() => this.refresherContent$.next()),
      showBackendToast(this.toastService),
    );
  }

  clearContent(dataRequestId: number) {
    return this.http.delete<IResponse<unknown>>(`${this.url}/${dataRequestId}/contents`).pipe(
      tap(() => this.refresherContent$.next()),
      showBackendToast(this.toastService),
    );
  }

  submitDataRequest(dataRequestId: number, fileUniqueNames?: string[]) {
    const params = fileUniqueNames ? new HttpParams({ fromObject: { fileUniqueNames } }) : {};
    return this.http.put<IResponse<unknown>>(`${this.url}/${dataRequestId}/submit`, {}, { params }).pipe(
      tap(() => this.refresher$.next()),
      showBackendToast(this.toastService),
    );
  }

  contentValidate(
    dataRequestId: number,
    dataRequestContent: Record<string, string | number | null | undefined>,
    dataPointName: string,
  ) {
    const body = {
      content: dataRequestContent,
      dataPointName: dataPointName,
    };

    return this.http.put<IResponse<DataPointValidationMessage>>(
      `${this.url}/${dataRequestId}/contents/validateDataPoint`,
      body,
    );
  }

  getDataRequestContributors(dataRequestId: number) {
    return this.http.get<IPagingResponse<DataRequestContributor>>(`${this.url}/${dataRequestId}/contributors`);
  }

  updateDataRequestContributors(dataRequestId: number, contributorIds: number[]) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${dataRequestId}/contributors`, contributorIds)
      .pipe(showBackendToast(this.toastService));
  }

  validateList(dataRequestId: number, body: number[]) {
    return this.http.put<IResponse<ContentValidationResult[]>>(`${this.url}/${dataRequestId}/contents/validate`, body);
  }

  downloadContentAs(dataRequestId: number, fileType: FileType) {
    return this.downloadService.download(`${this.url}/${dataRequestId}/contents/file`, fileType);
  }

  copyPreviousDataRequestContent(dataRequestId: number) {
    const body = {
      dataRequestId: dataRequestId,
    };
    return this.http.put<IResponse<unknown>>(`${this.url}/${dataRequestId}/copyPreviousDataRequestContent`, body).pipe(
      tap(() => this.refresherContent$.next()),
      showBackendToast(this.toastService),
    );
  }

  waive(id: number, waiveReason: string) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${id}/waive`, { waiveReason })
      .pipe(showBackendToast(this.toastService));
  }

  complete(id: number) {
    return this.http
      .put<IResponse<unknown>>(`${this.url}/${id}/complete`, {})
      .pipe(showBackendToast(this.toastService));
  }

  saveFieldChange(dataRequestId: number, fieldName: string, value: string | number | null, rowId?: number) {
    const body = {
      dataRequestId,
      fieldName,
      value,
      rowId,
    };
    return this.http
      .post<IResponse<unknown>>(`${this.url}/${dataRequestId}/contents/field`, body)
      .pipe(tap(() => this.refresherContent$.next()));
  }

  private convertDate(date: string) {
    const [day, month, year] = date.split('.');

    return `${year}-${month}-${day}`;
  }
}
